import React, { useState, useEffect } from "react";
import {
  LocalizationProvider,
  DateCalendar,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  Popper,
  Paper,
  Button,
  Box,
  Divider,
  TextField,
  Typography,
  Switch,
} from "@mui/material";

export default function CustomDateTimePicker({ value, onChange }) {
  const [anchorEl, setAnchorEl] = useState(null);
  // const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDate, setSelectedDate] = useState(value || null);

  const [time, setTime] = useState({ hh: "", mm: "", ampm: "AM" });
  const [use24Hr, setUse24Hr] = useState(false);
  useEffect(() => {
    setSelectedDate(value || null);

    if (value) {
      let hours = value.getHours();
      const minutes = value.getMinutes();

      if (!use24Hr) {
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;

        setTime({
          hh: String(hours).padStart(2, "0"),
          mm: String(minutes).padStart(2, "0"),
          ampm,
        });
      } else {
        setTime({
          hh: String(hours).padStart(2, "0"),
          mm: String(minutes).padStart(2, "0"),
          ampm: "",
        });
      }
    }
  }, [value]);
  const handleOpen = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleSave = () => {
    const hours =
      use24Hr || time.ampm === "AM"
        ? parseInt(time.hh || 0)
        : parseInt(time.hh || 0) + 12;
    const date = new Date(selectedDate);
    date.setHours(hours);
    date.setMinutes(parseInt(time.mm || 0));
    onChange(date);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "custom-popper" : undefined;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {/* Input Field */}
      <TextField
        placeholder="DD/MM/YYYY"
        value={
          selectedDate
            ? `${selectedDate.toLocaleDateString("en-GB")} ${String(
              time.hh
            ).padStart(2, "0")}:${String(time.mm).padStart(2, "0")} ${use24Hr ? "" : time.ampm
            }`
            : ""
        }
        onClick={handleOpen}
        fullWidth
        size="small"
        InputProps={{
          readOnly: true,
          style: {
            borderRadius: "8px",
            background: "#fff",
            height: "38px", // ⬅️ smaller input height
          },
        }}
      />

      {/* Custom Popper */}
      <Popper id={id} open={open} anchorEl={anchorEl} placement="top-start" modifiers={[
        {
          name: "offset",
          options: {
            offset: [0, 8], // [horizontal, vertical] → pushes popper 8px above input
          },
        },
      ]}>
        <Paper
          elevation={4}
          sx={{
            p: 1.5, // less padding for compactness
            borderRadius: "16px",
            width: 310,
            minHeight: "auto", // remove unnecessary height
            boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
            fontFamily: "Inter, sans-serif",
          }}
        >
          {/* Calendar */}
          <Box sx={{ pb: 0, mt: -0.5 }}>
            <DateCalendar
              value={selectedDate}
              onChange={(newDate) => {
                if (newDate) {
                  setSelectedDate(newDate);

                  // Get current time
                  const now = new Date();
                  let hours = now.getHours();
                  let minutes = now.getMinutes();

                  // Handle 12-hour vs 24-hour format
                  if (!use24Hr) {
                    const ampm = hours >= 12 ? "PM" : "AM";
                    hours = hours % 12 || 12;
                    setTime({
                      hh: String(hours).padStart(2, "0"),
                      mm: String(minutes).padStart(2, "0"),
                      ampm,
                    });
                  } else {
                    setTime({
                      hh: String(hours).padStart(2, "0"),
                      mm: String(minutes).padStart(2, "0"),
                      ampm: "",
                    });
                  }
                }
              }}
              sx={{
                transform: "scale(0.90)",
                transformOrigin: "top center",
                "& .MuiPickersDay-root": {
                  width: 40,
                  height: 45.5,
                  fontSize: "1rem",
                  margin: "0px",
                },
                "& .MuiDayCalendar-weekDayLabel": {
                  fontWeight: "700", // ⬅️ bold weekday labels
                  fontSize: "0.80rem",
                  padding: "0",
                },
                "& .MuiPickersCalendarHeader-root": {
                  mb: -0.6, // tighten spacing below header
                },
                "& .MuiPickersCalendarHeader-label": {
                  fontWeight: "700", // ⬅️ bold "October 2025"
                  fontSize: "1rem",
                },
                "& .MuiTypography-caption": {
                  fontSize: "0.65rem",
                },
                "& .MuiPickersArrowSwitcher-root button": {
                  padding: "2px",
                },
              }}
            />
          </Box>

          {/* 🔹 Thin horizontal line (no gap) */}
          <hr
            style={{
              border: "none",
              borderTop: "3px solid #044c92",
              margin: "2px 0 0 0", // remove extra space
            }}
          />

          {/* Time + toggle */}
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            mt={0.3}
            mb={0.3}
          >
            <Typography fontSize={13} fontWeight={500}>
              Time : Use 24-hour format
            </Typography>
            <Switch
              size="small"
              checked={use24Hr}
              onChange={(e) => setUse24Hr(e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: "#2ea4e7" },
                "& .MuiSwitch-track": { backgroundColor: "#2ea4e7" },
              }}
            />
          </Box>

          {/* Time fields */}
          <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
            <TextField
              placeholder="HH"
              type="number"
              size="small"
              value={time.hh}
              onChange={(e) => setTime({ ...time, hh: e.target.value.slice(0, 2) })}
              inputProps={{ min: 0, max: use24Hr ? 23 : 12 }}
              sx={{
                width: 66,
                "& input": { textAlign: "center", fontSize: "0.85rem" },
              }}
            />
            <Typography>:</Typography>
            <TextField
              placeholder="MM"
              type="number"
              size="small"
              value={time.mm}
              onChange={(e) => setTime({ ...time, mm: e.target.value.slice(0, 2) })}
              inputProps={{ min: 0, max: 59 }}
              sx={{
                width: 66,
                "& input": { textAlign: "center", fontSize: "0.85rem" },
              }}
            />
            {!use24Hr && (
              <TextField
                select
                SelectProps={{ native: true }}
                size="small"
                value={time.ampm}
                onChange={(e) => setTime({ ...time, ampm: e.target.value })}
                sx={{ width: 70, "& select": { fontSize: "0.85rem" } }}
              >
                <option>AM</option>
                <option>PM</option>
              </TextField>
            )}
          </Box>

          {/* 🔹 Bottom line (no extra gap) */}
          <hr
            style={{
              border: "none",
              borderTop: "3px solid #044c92",
              margin: "7px 0 3px 0",
            }}
          />

          {/* Action buttons */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.3}>
            <Button onClick={() => setSelectedDate(null)} sx={{ textTransform: "none", color: "#044c92", fontWeight: 'bold' }}>Clear</Button>
            <Button onClick={() => {
              const now = new Date();
              setSelectedDate(now);

              // Extract current hours/minutes
              let hours = now.getHours();
              let minutes = now.getMinutes();

              if (!use24Hr) {
                const ampm = hours >= 12 ? "PM" : "AM";
                hours = hours % 12 || 12; // convert 0 -> 12 for 12-hour format
                setTime({
                  hh: String(hours).padStart(2, "0"),
                  mm: String(minutes).padStart(2, "0"),
                  ampm,
                });
              } else {
                setTime({
                  hh: String(hours).padStart(2, "0"),
                  mm: String(minutes).padStart(2, "0"),
                  ampm: "", // unused in 24hr mode
                });
              }
            }} sx={{ textTransform: "none", color: "#044c92", fontWeight: 'bold' }}>Today</Button>
            <Button variant="contained" onClick={handleSave}
              sx={{
                textTransform: "none",
                background: "linear-gradient(to bottom, #044c92 , #0071bc , #022f5a )",
                "&:hover": { background: "linear-gradient(to bottom, #044c92 , #0071bc , #022f5a )" },
                borderRadius: "8px",
                px: 2,
                py: 0.5,
              }}>
              OK
            </Button>
          </Box>
        </Paper>

      </Popper>
    </LocalizationProvider>
  );
}
